import { Component, OnInit, OnDestroy, signal, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpParams } from '@angular/common/http';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil, switchMap } from 'rxjs/operators';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ButtonModule } from 'primeng/button';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { SelectModule } from 'primeng/select';
import { MessageService } from 'primeng/api';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { LabelComponent } from '../../../shared/components/form/label/label.component';
import { ClienteService } from '../../../services/cliente/cliente.service';
import { ProvinciaService } from '../../../services/provincia/provincia.service';
import { LocalidadService } from '../../../services/localidad/localidad.service';
import { AgenciaService } from '../../../services/agencia/agencia.service';
import { Cliente } from '../../../domain/cliente.domain';
import { Provincia } from '../../../domain/provincia.domain';
import { Localidad } from '../../../domain/localidad.domain';
import { ClienteAnunciantesPanelComponent } from '../../../components/clientes/cliente-anunciantes-panel/cliente-anunciantes-panel.component';

@Component({
  selector: 'app-cliente-edit',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    TextareaModule,
    ToggleSwitchModule,
    ButtonModule,
    AutoCompleteModule,
    SelectModule,
    ClienteAnunciantesPanelComponent
  ],
  templateUrl: './cliente-edit.component.html',
  styleUrls: ['./cliente-edit.component.scss']
})
export class ClienteEditComponent implements OnInit, OnDestroy {
  clienteForm!: FormGroup;
  loading = signal<boolean>(false);
  isEditMode = signal<boolean>(false);
  clienteId: string | null = null;
  cliente = signal<Cliente | null>(null);
  provincias = signal<Provincia[]>([]);
  filteredLocalidades = signal<Localidad[]>([]);
  localidadSuggestions = signal<Localidad[]>([]);
  agenciasProductoresDisponibles = signal<Cliente[]>([]);
  agenciasProductoresFiltradas = signal<Cliente[]>([]);
  showLocalidad = signal<boolean>(false);
  showAgenciaProductor = signal<boolean>(false);
  agenciasProductoresCargadas = signal<boolean>(false);
  cargandoAgenciasProductores = signal<boolean>(false);

  private destroy$ = new Subject<void>();

  tipoClienteOptions = [
    { label: 'Anunciante', value: 'anunciante' },
    { label: 'Agencia', value: 'agencia' },
    { label: 'Productor', value: 'productor' }
  ];

  condicionIvaOptions = [
    { label: 'Responsable Inscripto', value: 'responsable_inscripto' },
    { label: 'Monotributista', value: 'monotributista' },
    { label: 'Consumidor Final', value: 'consumidor_final' },
    { label: 'Exento', value: 'exento' },
    { label: 'Sujeto No Alcanzado / No Responsable', value: 'no_responsable' }
  ];

  private clienteService = inject(ClienteService);
  private provinciaService = inject(ProvinciaService);
  private localidadService = inject(LocalidadService);
  private agenciaService = inject(AgenciaService);
  private cdr = inject(ChangeDetectorRef);

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadProvincias();
    this.setupProvinciaListener();
    this.setupTipoClienteListener();
    this.setupRouteParamListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupRouteParamListener(): void {
    // Suscribirse a los cambios de parámetros de ruta
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const newId = params.get('id');
        // Solo recargar si el ID cambió
        if (newId !== this.clienteId) {
          this.loadCliente();
        }
      });
  }

  setupProvinciaListener(): void {
    this.clienteForm.get('provincia')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((provinciaId) => {
        if (provinciaId) {
          this.showLocalidad.set(true);
          this.loadLocalidadesByProvincia(provinciaId);
        } else {
          this.showLocalidad.set(false);
          this.filteredLocalidades.set([]);
          this.localidadSuggestions.set([]);
          this.clienteForm.patchValue({ localidad: null }, { emitEvent: false });
        }
      });
  }

  setupTipoClienteListener(): void {
    this.clienteForm.get('tipoCliente')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((tipoCliente) => {
        if (tipoCliente === 'anunciante') {
          this.showAgenciaProductor.set(true);
          // Cargar agencias/productores cuando cambia a anunciante
          if (!this.agenciasProductoresCargadas() && !this.cargandoAgenciasProductores()) {
            this.cargarAgenciasProductoresDisponibles();
          }
        } else {
          this.showAgenciaProductor.set(false);
          this.clienteForm.patchValue({ agenciaProductorCliente: null }, { emitEvent: false });
          // Limpiar cache cuando cambia el tipo de cliente
          this.agenciasProductoresCargadas.set(false);
          this.agenciasProductoresDisponibles.set([]);
          this.agenciasProductoresFiltradas.set([]);
        }
      });
  }

  cargarAgenciasProductoresDisponibles(callback?: () => void): void {
    // Si ya están cargadas, no volver a cargar
    if (this.agenciasProductoresCargadas()) {
      if (callback) {
        callback();
      }
      return;
    }

    // Si ya se está cargando, esperar a que termine
    if (this.cargandoAgenciasProductores()) {
      // Esperar un poco y volver a intentar
      setTimeout(() => {
        this.cargarAgenciasProductoresDisponibles(callback);
      }, 100);
      return;
    }

    this.cargandoAgenciasProductores.set(true);

    // Cargar agencias y productores en paralelo y combinar resultados
    // Limitamos a 50 de cada tipo para mejor performance
    const paramsAgencias = new HttpParams()
      .set('tipoCliente', 'agencia')
      .set('size', '50')
      .set('page', '1')
      .set('_order[razonSocial]', 'asc');
    
    const paramsProductores = new HttpParams()
      .set('tipoCliente', 'productor')
      .set('size', '50')
      .set('page', '1')
      .set('_order[razonSocial]', 'asc');
    
    forkJoin({
      agencias: this.clienteService.searchClientes(paramsAgencias),
      productores: this.clienteService.searchClientes(paramsProductores)
    }).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (responses: { agencias: Cliente[]; productores: Cliente[] }) => {
        const agencias = responses.agencias ?? [];
        const productores = responses.productores ?? [];

        const agenciasProductoresList = [...agencias, ...productores];
        
        // Ordenar por razón social
        agenciasProductoresList.sort((a: Cliente, b: Cliente) => {
          const razonA = a.razonSocial || '';
          const razonB = b.razonSocial || '';
          return razonA.localeCompare(razonB);
        });
        
        this.agenciasProductoresDisponibles.set(agenciasProductoresList);
        this.agenciasProductoresFiltradas.set(agenciasProductoresList);
        this.agenciasProductoresCargadas.set(true);
        this.cargandoAgenciasProductores.set(false);
        if (callback) {
          callback();
        }
      },
        error: (error: any) => {
          console.error('Error al cargar agencias/productores:', error);
          this.cargandoAgenciasProductores.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar las agencias/productores',
            life: 3000
          });
          if (callback) {
            callback();
          }
        }
    });
  }

  filtrarAgenciasProductores(event: any): void {
    const query = event.query?.toLowerCase() || '';
    const todasLasAgenciasProductores = this.agenciasProductoresDisponibles();
    const agenciasProductoresFiltradas = todasLasAgenciasProductores.filter(agenciaProductor => {
      const razonSocial = agenciaProductor.razonSocial?.toLowerCase() || '';
      const nombreFantasia = agenciaProductor.nombreFantasia?.toLowerCase() || '';
      return razonSocial.includes(query) || nombreFantasia.includes(query);
    });
    this.agenciasProductoresFiltradas.set(agenciasProductoresFiltradas);
  }

  /**
   * Obtiene el icono y tipo para un cliente agencia/productor
   */
  getAgenciaProductorIcon(cliente: Cliente): { icon: string; tipo: string } {
    if (!cliente.tipoCliente || cliente.tipoCliente.length === 0) {
      return { icon: 'pi pi-building', tipo: 'Agencia/Productor' };
    }
    
    const tipos = cliente.tipoCliente;
    if (tipos.includes('agencia')) {
      return { icon: 'pi pi-building', tipo: 'Agencia' };
    } else if (tipos.includes('productor')) {
      return { icon: 'pi pi-briefcase', tipo: 'Productor' };
    }
    
    return { icon: 'pi pi-building', tipo: 'Agencia/Productor' };
  }

  /**
   * Obtiene el label, icono y color para un tipo de cliente
   */
  getTipoClienteInfo(tipo: 'anunciante' | 'agencia' | 'productor'): { label: string; icon: string; color: string } {
    const tiposInfo: Record<'anunciante' | 'agencia' | 'productor', { label: string; icon: string; color: string }> = {
      anunciante: {
        label: 'Anunciante',
        icon: 'pi pi-megaphone',
        color: 'hsl(var(--primary))'
      },
      agencia: {
        label: 'Agencia',
        icon: 'pi pi-building',
        color: 'hsl(var(--success))'
      },
      productor: {
        label: 'Productor',
        icon: 'pi pi-briefcase',
        color: 'hsl(var(--warning))'
      }
    };
    return tiposInfo[tipo];
  }

  loadLocalidadesByProvincia(provinciaId: string | number, callback?: () => void): void {
    // Construir parámetros para filtrar por provincia y desactivar paginación
    // API Platform espera solo el ID de la provincia para el filtro exacto
    const params = new HttpParams()
      .set('provincia', provinciaId.toString())
      .set('pagination', 'false');

    this.localidadService.getAll(params).subscribe({
      next: (localidades: Localidad[]) => {
        this.filteredLocalidades.set(localidades);
        this.localidadSuggestions.set(localidades);
        if (callback) {
          callback();
        }
      },
      error: (error) => {
        console.error('Error al cargar localidades:', error);
        this.filteredLocalidades.set([]);
        this.localidadSuggestions.set([]);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar las localidades',
          life: 3000
        });
        if (callback) {
          callback();
        }
      }
    });
  }

  filterLocalidades(event: any): void {
    const query = event.query?.toLowerCase() || '';
    const allLocalidades = this.filteredLocalidades();
    const filtered = allLocalidades.filter(l => {
      const distritoNombre = l.distritoNombre?.toLowerCase() || '';
      return distritoNombre.includes(query);
    });
    // Actualizar las sugerencias con las localidades filtradas
    this.localidadSuggestions.set(filtered);
  }

  loadProvincias(): void {
    this.provinciaService.getAll().subscribe({
      next: (provincias: Provincia[]) => {
        this.provincias.set(provincias);
      },
      error: (error) => {
        console.error('Error al cargar provincias:', error);
        
        let errorMsg = 'Error al cargar las provincias';
        if (error.status === 401) {
          errorMsg = 'Sesión expirada. Por favor, inicia sesión nuevamente.';
        } else if (error.status === 403) {
          errorMsg = 'No tienes permisos para acceder a las provincias.';
        } else if (error.status === 0) {
          errorMsg = 'No se puede conectar con el servidor.';
        }
        
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMsg,
          life: 3000
        });
      }
    });
  }

  initForm(): void {
    this.clienteForm = this.fb.group({
      nombre: ['', Validators.required],
      apellido: ['', Validators.required],
      razonSocial: ['', Validators.required],
      nombreFantasia: [''],
      cuit: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      telefono: [''],
      domicilio: [''],
      provincia: [''],
      localidad: [''],
      tipoCliente: ['', Validators.required],
      agenciaProductorCliente: [''],
      condicionIva: [''],
      observaciones: [''],
      active: [true]
    });
  }

  loadCliente(): void {
    const newClienteId = this.route.snapshot.paramMap.get('id');
    
    // Limpiar estado anterior si cambió el ID
    if (newClienteId !== this.clienteId) {
      // Resetear formulario y estado
      this.clienteForm.reset();
      this.cliente.set(null);
      this.filteredLocalidades.set([]);
      this.localidadSuggestions.set([]);
      this.showLocalidad.set(false);
      this.showAgenciaProductor.set(false);
      this.agenciasProductoresCargadas.set(false);
      this.agenciasProductoresDisponibles.set([]);
      this.agenciasProductoresFiltradas.set([]);
    }
    
    this.clienteId = newClienteId;
    
    if (this.clienteId && this.clienteId !== 'nuevo') {
      this.isEditMode.set(true);
      this.loading.set(true);
      
      // Cargar el cliente primero
      this.clienteService.getById(this.clienteId!).pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: (cliente: Cliente) => {
          if (cliente) {
            this.cliente.set(cliente);
            
            // Solo cargar agencias/productores si el cliente es anunciante
            const esAnunciante = cliente.tipoCliente?.includes('anunciante') || false;
            if (esAnunciante) {
              this.showAgenciaProductor.set(true);
              // Cargar agencias/productores primero, luego procesar datos
              this.cargarAgenciasProductoresDisponibles(() => {
                this.procesarDatosCliente(cliente);
              });
            } else {
              this.procesarDatosCliente(cliente);
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Cliente no encontrado',
              life: 3000
            });
            this.router.navigate(['/clientes']);
          }
          this.loading.set(false);
        },
        error: (error) => {
          this.loading.set(false);
          console.error('Error al cargar cliente:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar los datos del cliente',
            life: 3000
          });
        }
      });
    } else {
      // En modo creación, cargar agencias/productores solo si el tipo es anunciante
      // Esto se maneja en setupTipoClienteListener
      this.isEditMode.set(false);
    }
  }

  private procesarDatosCliente(cliente: Cliente): void {
    let provinciaId: string | number | null = null;
    
    // Extract provincia ID from different formats
    if (typeof cliente.provincia === 'object' && cliente.provincia?.id) {
      // Convertir a número si es necesario para que coincida con optionValue
      if (typeof cliente.provincia.id === 'number') {
        provinciaId = cliente.provincia.id;
      } else if (typeof cliente.provincia.id === 'string') {
        provinciaId = parseInt(cliente.provincia.id, 10) || null;
      } else {
        provinciaId = cliente.provincia.id;
      }
    } else if (typeof cliente.provincia === 'string') {
      // Extract ID from IRI string like "/api/provincias/123"
      const match = cliente.provincia.match(/\/(\d+)$/);
      if (match) {
        provinciaId = parseInt(match[1], 10);
      }
    }
    
    // Establecer valores básicos primero
    this.clienteForm.patchValue({
      nombre: cliente.nombre || '',
      apellido: cliente.apellido || '',
      razonSocial: cliente.razonSocial || '',
      nombreFantasia: cliente.nombreFantasia || '',
      cuit: cliente.cuit || '',
      email: cliente.email || '',
      telefono: cliente.telefono || '',
      domicilio: cliente.domicilio || '',
      provincia: provinciaId || '',
      tipoCliente: cliente.tipoCliente?.[0] || '',
      condicionIva: cliente.condicionIva || '',
      observaciones: cliente.observaciones || '',
      active: cliente.active ?? true
    }, { emitEvent: false }); // No emitir eventos para evitar que se dispare el listener de tipoCliente
    
    // Forzar detección de cambios para asegurar que los valores se reflejen en la vista
    this.cdr.detectChanges();

    // Establecer agenciaProductor si existe (las agencias/productores ya están cargadas)
    if (cliente.agenciaProductor) {
      let agenciaProductorId = '';
      let agenciaProductorObjeto: Cliente | null = null;
      
      // Si agenciaProductor es un objeto Cliente, extraer el ID y el objeto completo
      if (typeof cliente.agenciaProductor === 'object' && cliente.agenciaProductor.id) {
        agenciaProductorId = cliente.agenciaProductor.id.toString();
        agenciaProductorObjeto = cliente.agenciaProductor as Cliente;
      } else if (typeof cliente.agenciaProductor === 'string') {
        // Si es un string IRI, extraer el ID
        const match = cliente.agenciaProductor.match(/\/(\d+)$/);
        if (match) {
          agenciaProductorId = match[1];
        }
      }
      
      // Buscar la agencia/productor en la lista y establecer el valor
      if (agenciaProductorId) {
        // Si tenemos el objeto completo del backend, usarlo directamente
        if (agenciaProductorObjeto) {
          // Asegurar que el objeto esté en la lista de sugerencias
          const existeEnLista = this.agenciasProductoresDisponibles().some(
            ap => ap.id?.toString() === agenciaProductorObjeto!.id?.toString()
          );
          if (!existeEnLista) {
            const listaActual = this.agenciasProductoresDisponibles();
            const nuevaLista = [...listaActual, agenciaProductorObjeto];
            nuevaLista.sort((a: Cliente, b: Cliente) => {
              const razonA = a.razonSocial || '';
              const razonB = b.razonSocial || '';
              return razonA.localeCompare(razonB);
            });
          this.agenciasProductoresDisponibles.set(nuevaLista);
          this.agenciasProductoresFiltradas.set(nuevaLista);
        }
        this.clienteForm.patchValue({ agenciaProductorCliente: agenciaProductorObjeto }, { emitEvent: false });
        this.cdr.detectChanges();
        } else {
          // Si solo tenemos el ID, buscar en la lista cargada
          const agenciaProductorEncontrado = this.agenciasProductoresDisponibles().find(
            agenciaProductor => agenciaProductor.id?.toString() === agenciaProductorId
          );
          if (agenciaProductorEncontrado) {
            this.clienteForm.patchValue({ agenciaProductorCliente: agenciaProductorEncontrado }, { emitEvent: false });
            this.cdr.detectChanges();
          } else {
            // Si no se encuentra en la lista, cargar el objeto completo desde el backend
            // Esto puede pasar si el agencia/productor no está en las primeras 50
            this.clienteService.getById(agenciaProductorId).subscribe({
              next: (agenciaProductor: Cliente) => {
                // Agregar a la lista para que esté disponible
                const listaActual = this.agenciasProductoresDisponibles();
                const nuevaLista = [...listaActual, agenciaProductor];
                nuevaLista.sort((a: Cliente, b: Cliente) => {
                  const razonA = a.razonSocial || '';
                  const razonB = b.razonSocial || '';
                  return razonA.localeCompare(razonB);
                });
                this.agenciasProductoresDisponibles.set(nuevaLista);
                this.agenciasProductoresFiltradas.set(nuevaLista);
                this.clienteForm.patchValue({ agenciaProductorCliente: agenciaProductor }, { emitEvent: false });
                this.cdr.detectChanges();
              },
              error: (error) => {
                console.warn('No se pudo cargar el agencia/productor:', error);
              }
            });
          }
        }
      }
    }

    // Cargar localidades de la provincia seleccionada antes de establecer la localidad
    if (provinciaId) {
      this.showLocalidad.set(true);
      this.loadLocalidadesByProvincia(provinciaId, () => {
        // Callback para establecer la localidad después de cargar
        if (cliente.localidad) {
          let localidadId = '';
          if (typeof cliente.localidad === 'string') {
            // Extract ID from IRI string like "/api/localidades/123"
            const match = cliente.localidad.match(/\/(\d+)$/);
            if (match) {
              localidadId = match[1];
            }
          } else if (typeof cliente.localidad === 'object' && cliente.localidad?.id) {
            localidadId = cliente.localidad.id.toString();
          }
          
          if (localidadId) {
            const foundLocalidad = this.filteredLocalidades().find(l => l.id?.toString() === localidadId);
            if (foundLocalidad) {
              this.clienteForm.patchValue({ localidad: foundLocalidad }, { emitEvent: false });
              this.cdr.detectChanges();
            }
          }
        }
      });
    }
  }

  onSubmit(): void {
    if (this.clienteForm.invalid) {
      this.markFormGroupTouched(this.clienteForm);
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulario Inválido',
        detail: 'Por favor complete todos los campos requeridos',
        life: 3000
      });
      return;
    }

    const formValue = this.clienteForm.value;
    
    // Construct provincia IRI if a provincia is selected
    let provinciaValue: string | null = null;
    if (formValue.provincia) {
      const provinciaId = formValue.provincia;
      provinciaValue = `/api/provincias/${provinciaId}`;
    }

    // Construct localidad IRI if a localidad is selected
    let localidadValue: string | null = null;
    if (formValue.localidad) {
      const localidadObj = formValue.localidad;
      let localidadId: string | number | null = null;
      
      if (typeof localidadObj === 'object' && localidadObj !== null && localidadObj.id) {
        // Si es un objeto con ID, usar el ID numérico
        localidadId = localidadObj.id;
      } else if (typeof localidadObj === 'string') {
        // Si es un string, puede ser un IRI o un ID numérico
        const match = localidadObj.match(/\/(\d+)$/);
        if (match) {
          localidadId = match[1];
        } else if (/^\d+$/.test(localidadObj)) {
          // Si es solo un número como string
          localidadId = localidadObj;
        }
      } else if (typeof localidadObj === 'number') {
        localidadId = localidadObj;
      }
      
      // Solo crear el IRI si tenemos un ID numérico válido
      if (localidadId && (typeof localidadId === 'number' || /^\d+$/.test(String(localidadId)))) {
        localidadValue = `/api/localidades/${localidadId}`;
      }
    }
    
    // Construir IRI de agenciaProductor si está seleccionado
    let agenciaProductorIri: string | null = null;
    if (formValue.agenciaProductorCliente) {
      let agenciaProductorId: string | number | null = null;
      
      if (typeof formValue.agenciaProductorCliente === 'object' && formValue.agenciaProductorCliente !== null && formValue.agenciaProductorCliente.id) {
        // Si es un objeto Cliente con ID, usar el ID numérico
        agenciaProductorId = formValue.agenciaProductorCliente.id;
      } else if (typeof formValue.agenciaProductorCliente === 'string') {
        // Si es un string, puede ser un IRI o un ID numérico
        const match = formValue.agenciaProductorCliente.match(/\/(\d+)$/);
        if (match) {
          agenciaProductorId = match[1];
        } else if (/^\d+$/.test(formValue.agenciaProductorCliente)) {
          // Si es solo un número como string
          agenciaProductorId = formValue.agenciaProductorCliente;
        }
      } else if (typeof formValue.agenciaProductorCliente === 'number') {
        agenciaProductorId = formValue.agenciaProductorCliente;
      }
      
      // Solo crear el IRI si tenemos un ID numérico válido
      if (agenciaProductorId && (typeof agenciaProductorId === 'number' || /^\d+$/.test(String(agenciaProductorId)))) {
        agenciaProductorIri = `/api/clientes/${agenciaProductorId}`;
      }
    }
    
    const cliente: Cliente = {
      nombre: formValue.nombre,
      apellido: formValue.apellido,
      razonSocial: formValue.razonSocial,
      nombreFantasia: formValue.nombreFantasia,
      cuit: formValue.cuit,
      email: formValue.email,
      telefono: formValue.telefono,
      domicilio: formValue.domicilio,
      provincia: provinciaValue || undefined,
      localidad: localidadValue || undefined,
      tipoCliente: formValue.tipoCliente ? [formValue.tipoCliente] : [],
      agenciaProductor: agenciaProductorIri || undefined,
      condicionIva: formValue.condicionIva ? formValue.condicionIva : null,
      observaciones: formValue.observaciones || undefined,
      active: formValue.active
    };

    if (this.isEditMode()) {
      cliente.id = this.clienteId || undefined;
    }

    this.loading.set(true);
    
    const operation = this.isEditMode() 
      ? this.clienteService.update(this.clienteId!, cliente)
      : this.clienteService.create(cliente);

    operation.subscribe({
      next: (response) => {
        this.loading.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: this.isEditMode() ? 'Cliente actualizado correctamente' : 'Cliente creado correctamente',
          life: 3000
        });
        setTimeout(() => {
          this.router.navigate(['/clientes']);
        }, 1500);
      },
      error: (error) => {
        this.loading.set(false);
        console.error('Error al guardar cliente:', error);
        
        let errorMessage = 'Error al guardar el cliente';
        
        if (error.status === 422 && error.error?.violations) {
          const violations = error.error.violations;
          const fieldErrors: { [key: string]: string } = {};
          
          violations.forEach((violation: any) => {
            const field = violation.propertyPath;
            const message = violation.message;
            fieldErrors[field] = message;
            
            if (field === 'cuit') {
              this.clienteForm.get('cuit')?.setErrors({ 'server': message });
              this.clienteForm.get('cuit')?.markAsTouched();
            }
          });
          
          if (violations.length === 1) {
            errorMessage = violations[0].message;
          } else {
            errorMessage = `Se encontraron ${violations.length} errores de validación`;
          }
        } else if (error.error?.detail) {
          errorMessage = error.error.detail;
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }
        
        this.messageService.add({
          severity: 'error',
          summary: 'Error de Validación',
          detail: errorMessage,
          life: 5000
        });
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/clientes']);
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  get pageTitle(): string {
    return this.isEditMode() ? 'Editar Cliente' : 'Nuevo Cliente';
  }
}
